import { Trans } from "@lingui/macro";
import cx from "classnames";
import { useMemo } from "react";

import { selectAccount, selectPositionsInfoData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { formatUsd } from "lib/numbers";

const HEADING = "usdx-display mb-9 text-[17px] uppercase tracking-[1.4px] opacity-90";
const ROW = "flex justify-between text-[13.5px]";
const ACTION =
  "usdx-glass usdx-glass-hover rounded-full py-9 text-[12.5px] font-semibold transition-[background,transform] duration-200";

/**
 * Account equity card from the design, driven by the real open positions.
 *
 * Everything here is derived from `positionsInfo`; with no wallet connected the
 * figures read as em-dashes rather than showing placeholder numbers.
 */
export function UsdxAccountPanel({ className }: { className?: string }) {
  const account = useSelector(selectAccount);
  const positionsInfoData = useSelector(selectPositionsInfoData);

  const stats = useMemo(() => {
    const positions = Object.values(positionsInfoData ?? {});

    if (!account || !positions.length) {
      return { collateral: 0n, pnl: 0n, netValue: 0n, sizeInUsd: 0n, hasPositions: false };
    }

    let collateral = 0n;
    let pnl = 0n;
    let netValue = 0n;
    let sizeInUsd = 0n;

    for (const position of positions) {
      collateral += position.collateralUsd ?? 0n;
      pnl += position.pnl ?? 0n;
      netValue += position.netValue ?? 0n;
      sizeInUsd += position.sizeInUsd ?? 0n;
    }

    return { collateral, pnl, netValue, sizeInUsd, hasPositions: true };
  }, [account, positionsInfoData]);

  /*
   * Margin usage = notional against the collateral backing it, which is the
   * meaningful ratio for a cross-margin perp account.
   */
  const marginUsage = useMemo(() => {
    if (!stats.hasPositions || stats.collateral <= 0n) {
      return 0;
    }

    const ratio = Number((stats.sizeInUsd * 10_000n) / stats.collateral) / 100;

    return Math.min(100, ratio);
  }, [stats]);

  const isConnected = Boolean(account);
  const pnlIsUp = stats.pnl >= 0n;

  const show = (value: bigint) => (isConnected ? formatUsd(value, { displayDecimals: 2 }) : "—");

  return (
    <section className={cx("usdx-account-card rounded-10 p-16 text-white", className)}>
      <div className="mb-16 grid grid-cols-3 gap-8">
        <button type="button" className={ACTION}>
          <Trans>Deposit</Trans>
        </button>
        <button type="button" className={ACTION}>
          <Trans>Withdraw</Trans>
        </button>
        <button type="button" className={ACTION}>
          <Trans>Transfer</Trans>
        </button>
      </div>

      <div className={HEADING}>
        <Trans>Account Equity</Trans>
      </div>
      <div className="mb-16 flex flex-col gap-7">
        <div className={ROW}>
          <span className="opacity-80">
            <Trans>Collateral</Trans>
          </span>
          <span className="font-semibold numbers">{show(stats.collateral)}</span>
        </div>
        <div className={ROW}>
          <span className="opacity-80">
            <Trans>Net value</Trans>
          </span>
          <span className="font-semibold numbers">{show(stats.netValue)}</span>
        </div>
      </div>

      <div className={HEADING}>
        <Trans>USDX Overview</Trans>
      </div>
      <div className="flex flex-col gap-7">
        <div className={ROW}>
          <span className="opacity-80">
            <Trans>Position size</Trans>
          </span>
          <span className="font-semibold numbers">{show(stats.sizeInUsd)}</span>
        </div>
        <div className={ROW}>
          <span className="opacity-80">
            <Trans>Unrealized PNL</Trans>
          </span>
          <span
            className="font-bold numbers"
            style={{ color: isConnected && !pnlIsUp ? "#ffd6d6" : "#c9ffd6" }}
          >
            {isConnected
              ? `${pnlIsUp ? "+" : "-"}${formatUsd(stats.pnl < 0n ? -stats.pnl : stats.pnl, { displayDecimals: 2 })}`
              : "—"}
          </span>
        </div>
        <div className={ROW}>
          <span className="opacity-80">
            <Trans>Margin usage</Trans>
          </span>
          <span className="font-semibold numbers">{isConnected ? `${marginUsage.toFixed(1)}%` : "—"}</span>
        </div>
      </div>

      <div className="mt-12 h-6 overflow-hidden rounded-full bg-white/25">
        <div
          className="h-full rounded-full bg-white transition-[width] duration-500"
          style={{ width: `${marginUsage}%` }}
        />
      </div>
    </section>
  );
}
